'use client';

import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import {
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { cn } from '../../lib/cn';
import { Popover, PopoverContent, PopoverTrigger } from '../../primitive/popover';
import { ScrollArea } from '../../primitive/scroll-area';

import { Badge } from '../badge';
import { buttonVariants } from '../button';
import { Input } from '../input';

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxTagInputProps {
  value: string[];
  onChange: (values: string[]) => void;
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  avoidCollisions?: boolean;
  ariaLabel?: string;
  searchAriaLabel?: string;
}

export function ComboboxTagInput({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  className,
  disabled = false,
  avoidCollisions = false,
  ariaLabel = '태그 선택',
  searchAriaLabel = '태그 검색',
}: ComboboxTagInputProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number>();

  useEffect(() => {
    if (!triggerRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      setTriggerWidth(entries[0]?.contentRect.width);
    });

    resizeObserver.observe(triggerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!open || !inputRef.current) {
      return;
    }

    const timer = window.setTimeout(() => inputRef.current?.focus(), 100);
    return () => window.clearTimeout(timer);
  }, [open]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) {
      return options;
    }

    const searchLower = search.toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(searchLower));
  }, [options, search]);

  const optionMap = useMemo(
    () => new Map(options.map((option) => [option.value, option.label])),
    [options],
  );

  const handleSelect = (optionValue: string) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((entry) => entry !== optionValue)
        : [...value, optionValue],
    );
  };

  const handleRemove = (optionValue: string, event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onChange(value.filter((entry) => entry !== optionValue));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      setSearch('');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            buttonVariants({ variant: 'outline' }),
            'h-auto min-h-9 w-full justify-between p-2',
            disabled && 'cursor-not-allowed opacity-50',
            className,
          )}
        >
          <div className="flex flex-1 flex-wrap items-center gap-1">
            {value.length > 0 ? (
              value.map((entry) => (
                <Badge key={entry} variant="secondary" className="flex items-center gap-1">
                  <span>{optionMap.get(entry) ?? entry}</span>
                  {!disabled ? (
                    <div
                      role="button"
                      aria-label={`${optionMap.get(entry) ?? entry} 제거`}
                      tabIndex={0}
                      className="cursor-pointer rounded-sm text-sm font-normal hover:bg-gray-200"
                      onClick={(event) => handleRemove(entry, event)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleRemove(entry, event as unknown as MouseEvent);
                        }
                      }}
                    >
                      <XIcon className="size-3" />
                    </div>
                  ) : null}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        aria-label={ariaLabel}
        className="p-0"
        style={{ width: triggerWidth }}
        align="start"
        avoidCollisions={avoidCollisions}
      >
        <div className="flex flex-col">
          <div className="border-b p-2">
            <Input
              ref={inputRef}
              value={search}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              aria-label={searchAriaLabel}
              className="h-8"
            />
          </div>

          <ScrollArea className="max-h-[200px]">
            {filteredOptions.length === 0 ? (
              <div className="text-muted-foreground p-4 text-center text-sm">{emptyMessage}</div>
            ) : (
              <div className="p-1">
                {filteredOptions.map((option) => {
                  const isSelected = value.includes(option.value);

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSelect(option.value)}
                      className={cn(
                        'hover:bg-accent hover:text-accent-foreground flex w-full cursor-pointer items-center justify-between rounded-sm px-2 py-1.5 text-sm font-normal outline-none',
                        isSelected && 'bg-accent/50',
                      )}
                    >
                      <span>{option.label}</span>
                      {isSelected ? <CheckIcon className="size-4" /> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
}
